import { PlusIcon, Trash2Icon, XIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { LoadFailed, LoadingLine } from '../ui/LoadState';
import { useAdmin } from '../AdminContext';
import type { SegmentsState } from './useSegments';
import { skinTypes } from '../../data/concerns';
import { btnGhost, btnIcon, btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import { SEGMENT_RULES_MAX, contradiction, ruleProblem } from '../../core/rules/segments';
import type { SegmentDefinition, SegmentRule } from '../../core/types/mail';
type Kind = SegmentRule['kind'];
const kinds: {
  kind: Kind;
  label: string;
}[] = [{
  kind: 'customer',
  label: 'Klant of niet'
}, {
  kind: 'boughtProduct',
  label: 'Kocht product'
}, {
  kind: 'subscription',
  label: 'Abonnement'
}, {
  kind: 'skinType',
  label: 'Huidtype'
}, {
  kind: 'lastVisitWeeks',
  label: 'Laatste bezoek'
}];
const fresh = (kind: Kind, productId: string): SegmentRule => {
  switch (kind) {
    case 'customer':
      return {
        kind,
        value: true
      };
    case 'boughtProduct':
      return {
        kind,
        productId
      };
    case 'subscription':
      return {
        kind,
        value: true
      };
    case 'skinType':
      return {
        kind,
        skinType: skinTypes[0]
      };
    case 'lastVisitWeeks':
      return {
        kind,
        weeks: 12
      };
  }
};
const selectCls = `${inputCls} py-2`;

/** Doelgroepen samenstellen: alle regels samen moeten kloppen. Het aantal komt van de server. */
export function Segments({
  seg
}: {
  seg: SegmentsState;
}) {
  const {
    catalog,
    index
  } = useAdmin();
  const products = catalog.products.filter(p => p.kind !== 'giftcard');
  const firstProduct = products[0]?.id ?? '';
  const update = (id: string, patch: Partial<SegmentDefinition>) => seg.edit(list => list.map(s => s.id === id ? {
    ...s,
    ...patch
  } : s));
  const setRule = (s: SegmentDefinition, i: number, rule: SegmentRule) => update(s.id, {
    rules: s.rules.map((r, j) => j === i ? rule : r)
  });
  const add = () => seg.edit(list => [...list, {
    id: `seg-${Date.now().toString(36)}`,
    name: 'Nieuwe doelgroep',
    rules: [{
      kind: 'customer',
      value: true
    }]
  }]);
  const problems = seg.items.flatMap(s => s.rules.map(r => ruleProblem(r, id => Boolean(index.productById(id)))).filter(Boolean));
  const blocked = problems.length > 0 || seg.items.some(s => !s.name.trim());
  return <Panel title="Doelgroepen" description={seg.list ? `Van de ${seg.list.total} bevestigde lezers. Kies er een bij de nieuwsbrief.` : 'Kies er een bij de nieuwsbrief.'} actions={<button type="button" onClick={add} className={btnGhost} data-id="element-2675" data-mp="2tz2q2iepvm4o">
          <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-2676" data-mp="2tzcei1k45l89" />
          Nieuw
        </button>} data-id="element-2674" data-mp="3mnqbcdzgubi8">
      {seg.status === 'error' ? <LoadFailed variant="inline" title="De doelgroepen konden niet laden" onRetry={seg.reload} data-id="element-2677" data-mp="33p5t7bfjyggq" /> : !seg.list ? <LoadingLine label="Doelgroepen laden…" data-id="element-2678" data-mp="268yv0zflgupa" /> : <ul className="space-y-5" data-id="element-2679" data-mp="2b81nzliavagw">
          {seg.items.map(s => {
        const count = seg.countOf(s);
        const clash = contradiction(s.rules);
        return <li key={s.id} className="rounded-md border border-line p-3 sm:p-4" data-id="element-2680" data-mp="1yq8ttv6xs612">
                <div className="flex items-center gap-2" data-id="element-2681" data-mp="1yo4or9xax0z7">
                  <label className="min-w-0 flex-1" data-id="element-2682" data-mp="1tpl7hmtnkdd6">
                    <span className="sr-only" data-id="element-2683" data-mp="1os1q4e1nrkh0">Naam van de doelgroep</span>
                    <input value={s.name} onChange={e => update(s.id, {
                name: e.target.value
              })} className={`${inputCls} font-medium`} aria-invalid={!s.name.trim()} aria-describedby={!s.name.trim() ? `${s.id}-naam` : undefined} data-id="element-2684" data-mp="1otw4f9ifcec4" />
                  </label>
                  <span className="shrink-0 text-[13px] tabular-nums text-muted" aria-live="polite" data-id="element-2685" data-mp="1634vg92cmpy9">
                    {count === null ? 'telt na opslaan' : `${count} lezers`}
                  </span>
                  <button type="button" onClick={() => seg.edit(list => list.filter(x => x.id !== s.id))} className={btnIcon} aria-label={`${s.name || 'Doelgroep'} verwijderen`} data-id="element-2686" data-mp="x2q8z6dnf4t6">
                    <Trash2Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-2687" data-mp="1qpur3hu1q5v8" />
                  </button>
                </div>
                {!s.name.trim() && <p id={`${s.id}-naam`} className="mt-1 text-[12px] text-clay" data-id="element-2688" data-mp="1gzhx6z0cd0cf">
                    Geef de doelgroep een naam.
                  </p>}
                <ol className="mt-3 space-y-2" data-id="element-2689" data-mp="17zlerf4joxpt">
                  {s.rules.map((r, i) => <li key={i} className="flex flex-wrap items-center gap-2" data-id="element-2690" data-mp="u03muqvyd4c8" data-mpi={i}>
                      <span className="w-6 text-[12px] text-muted" data-id="element-2691" data-mp="u26h5n451jmr">{i === 0 ? 'Wie' : 'en'}</span>
                      <label className="min-w-[9rem] flex-1 sm:flex-none" data-id="element-2692" data-mp="y3rv7bses3yj">
                        <span className="sr-only" data-id="element-2693" data-mp="2oemlgiddinoz">Soort regel</span>
                        <select value={r.kind} onChange={e => setRule(s, i, fresh(e.target.value as Kind, firstProduct))} className={selectCls} data-id="element-2694" data-mp="2sfzv73jl7b16">
                          {kinds.map(k => <option key={k.kind} value={k.kind} data-id="element-2695" data-mp="crkhh6phf2ts">
                              {k.label}
                            </option>)}
                        </select>
                      </label>
                      <RuleValue rule={r} onChange={rule => setRule(s, i, rule)} products={products} data-id="element-2696" data-mp="lpwim2nz5j0h" />
                      {s.rules.length > 1 && <button type="button" onClick={() => update(s.id, {
                rules: s.rules.filter((_, j) => j !== i)
              })} className={btnIcon} aria-label="Regel verwijderen" data-id="element-2697" data-mp="vhiy4b7ci491">
                          <XIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-2698" data-mp="19eo4kcaz154t" />
                        </button>}
                    </li>)}
                </ol>
                {s.rules.length < SEGMENT_RULES_MAX && <button type="button" onClick={() => update(s.id, {
            rules: [...s.rules, fresh('skinType', firstProduct)]
          })} className="mt-2 min-h-[40px] text-[13px] text-clay hover:text-ink" data-id="element-2699" data-mp="36avc96e36bxh">
                    + regel
                  </button>}
                {clash && <p className="mt-2 text-[12px] text-clay" data-id="element-2700" data-mp="1k3adm4a8ue7z">{clash}</p>}
              </li>;
      })}
        </ul>}

      {seg.dirty && <div className="mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-line pt-4" data-id="element-2701" data-mp="1t51ij6wymh0s">
          {blocked && <p className="mr-auto text-[12px] text-clay" data-id="element-2702" data-mp="1y3uyr73msm4n">Geef elke groep een naam en kies bestaande producten.</p>}
          <button type="button" onClick={seg.discard} className={btnSecondary} data-id="element-2703" data-mp="22ul1sdol4cw6">
            Verwerpen
          </button>
          <button type="button" onClick={seg.save} disabled={seg.saving || blocked} className={btnPrimary} data-id="element-2704" data-mp="27smln0tu4g8p">
            {seg.saving ? 'Opslaan…' : 'Doelgroepen opslaan'}
          </button>
        </div>}
    </Panel>;
}
function RuleValue({
  rule,
  onChange,
  products
}: {
  rule: SegmentRule;
  onChange: (r: SegmentRule) => void;
  products: {
    id: string;
    brand: string;
    name: string;
  }[];
}) {
  const wrap = 'min-w-[9rem] flex-1';
  switch (rule.kind) {
    case 'customer':
    case 'subscription':
      return <label className={wrap} data-id="element-2705" data-mp="3sc8jvpe870gt">
          <span className="sr-only" data-id="element-2706" data-mp="3ncvnoe1gsh1k">Waarde</span>
          <select value={rule.value ? 'ja' : 'nee'} onChange={e => onChange({
          ...rule,
          value: e.target.value === 'ja'
        })} className={selectCls} data-id="element-2707" data-mp="3ne6xauhlcj0l">
            <option value="ja" data-id="element-2708" data-mp="jw58b8jzsrn7">{rule.kind === 'customer' ? 'is klant' : 'heeft abonnement'}</option>
            <option value="nee" data-id="element-2709" data-mp="f5ggfs4ehsnj">{rule.kind === 'customer' ? 'is nog geen klant' : 'heeft geen abonnement'}</option>
          </select>
        </label>;
    case 'boughtProduct':
      return <label className={wrap} data-id="element-2710" data-mp="17lr920g0jexb">
          <span className="sr-only" data-id="element-2711" data-mp="17msw73aazrve">Product</span>
          <select value={rule.productId} onChange={e => onChange({
          ...rule,
          productId: e.target.value
        })} className={selectCls} data-id="element-2712" data-mp="17nkhnla6s97y">
            {products.map(p => <option key={p.id} value={p.id} data-id="element-2713" data-mp="b5vd9npsf0e6">
                {p.brand} {p.name}
              </option>)}
          </select>
        </label>;
    case 'skinType':
      return <label className={wrap} data-id="element-2714" data-mp="1alct4f1f12i">
          <span className="sr-only" data-id="element-2715" data-mp="17flrjmulp45">Huidtype</span>
          <select value={rule.skinType} onChange={e => onChange({
          ...rule,
          skinType: e.target.value as typeof rule.skinType
        })} className={selectCls} data-id="element-2716" data-mp="69eocrarki01">
            {skinTypes.map(t => <option key={t} value={t} data-id="element-2717" data-mp="yxagdipe1lqh">
                {t}
              </option>)}
          </select>
        </label>;
    case 'lastVisitWeeks':
      return <label className={`${wrap} flex items-center gap-2 text-[13px] text-muted`} data-id="element-2718" data-mp="oybjsyhwdgju">
          <span data-id="element-2719" data-mp="35ni61f8vvs9a">langer dan</span>
          <input type="number" inputMode="numeric" min={1} max={104} value={rule.weeks} onChange={e => onChange({
          ...rule,
          weeks: Math.round(Number(e.target.value) || 0)
        })} className={`${inputCls} w-20 py-2`} aria-label="Aantal weken" data-id="element-2720" data-mp="35mfcezk6fbpo" />
          <span data-id="element-2721" data-mp="2x8kjbphp7s5j">weken geleden</span>
        </label>;
  }
}